import React, { useState, useEffect, useCallback, useRef } from 'react';
import { activationCodesAPI, plansAPI, resellersAPI } from '../../services/api';
import Modal from '../../components/common/Modal';
import Spinner from '../../components/common/Spinner';
import Pagination from '../../components/common/Pagination';
import toast from 'react-hot-toast';

const StatusBadge = ({ isUsed }) => (
  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
    isUsed ? 'bg-gray-100 text-gray-500' : 'bg-green-100 text-green-700'
  }`}>
    {isUsed ? '✓ Used' : '● Available'}
  </span>
);

const EMPTY_FORM = { resellerId: '', planId: '', count: 10 };

const ActivationCodes = () => {
  const [codes, setCodes]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showModal, setShowModal]   = useState(false);
  const [saving, setSaving]         = useState(false);
  const [exporting, setExporting]   = useState(false);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [resellers, setResellers]   = useState([]);
  const [plans, setPlans]           = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [filters, setFilters]       = useState({ isUsed: '', resellerId: '', planId: '' });
  const [copiedCode, setCopiedCode] = useState(null);

  // Keep a stable ref to pagination.limit so fetchCodes dependency stays clean
  const limitRef = useRef(20);

  /* ── Fetch one page ─────────────────────────────────────────────────────── */
  const fetchCodes = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = { page, limit: limitRef.current };
      if (filters.isUsed !== '')  params.isUsed     = filters.isUsed;
      if (filters.resellerId)     params.resellerId = filters.resellerId;
      if (filters.planId)         params.planId     = filters.planId;

      const res = await activationCodesAPI.getAll(params);
      setCodes(res.data.data);
      setPagination(res.data.pagination);
    } catch {
      toast.error('Failed to load activation codes');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  /* ── Fetch resellers + plans for dropdowns ──────────────────────────────── */
  const fetchMeta = async () => {
    try {
      const [rRes, pRes] = await Promise.all([
        resellersAPI.getAll({ isActive: true, limit: 1000 }),
        plansAPI.getAll({ isActive: true }),
      ]);
      setResellers(rRes.data.data);
      setPlans(pRes.data.data);
    } catch {
      toast.error('Failed to load resellers / plans');
    }
  };

  useEffect(() => { fetchMeta(); }, []);
  useEffect(() => { fetchCodes(1); }, [fetchCodes]);

  /* ── Export all matching records as CSV ─────────────────────────────────── */
  const handleExport = async () => {
    setExporting(true);
    try {
      // Fetch up to 10 000 rows with current filters (no pagination cap)
      const params = { page: 1, limit: 10000 };
      if (filters.isUsed !== '')  params.isUsed     = filters.isUsed;
      if (filters.resellerId)     params.resellerId = filters.resellerId;
      if (filters.planId)         params.planId     = filters.planId;

      const res  = await activationCodesAPI.getAll(params);
      const rows = res.data.data;

      if (!rows.length) {
        toast.error('No codes to export with current filters');
        return;
      }

      // Build CSV
      const headers = [
        'Code', 'Plan', 'Billing Cycle', 'Plan Price',
        'Reseller Name', 'Reseller Email',
        'Status', 'Used By Name', 'Used By Email', 'Used At', 'Created At',
      ];

      const escape = (v) => {
        if (v == null) return '';
        const s = String(v);
        return s.includes(',') || s.includes('"') || s.includes('\n')
          ? `"${s.replace(/"/g, '""')}"`
          : s;
      };

      const csvRows = [
        headers.join(','),
        ...rows.map(c => [
          escape(c.code),
          escape(c.plan?.name),
          escape(c.plan?.billingCycle),
          escape(c.plan?.price),
          escape(c.reseller?.name),
          escape(c.reseller?.email),
          escape(c.isUsed ? 'Used' : 'Available'),
          escape(c.usedBy?.name),
          escape(c.usedBy?.email),
          escape(c.usedAt  ? new Date(c.usedAt).toLocaleDateString()  : ''),
          escape(c.createdAt ? new Date(c.createdAt).toLocaleDateString() : ''),
        ].join(',')),
      ];

      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;

      // Include filter context in the filename
      const statusLabel   = filters.isUsed === 'true' ? '_used' : filters.isUsed === 'false' ? '_available' : '';
      const dateStr       = new Date().toISOString().split('T')[0];
      a.download          = `activation_codes${statusLabel}_${dateStr}.csv`;

      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${rows.length} code${rows.length !== 1 ? 's' : ''}`);
    } catch {
      toast.error('Export failed. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  /* ── Generate codes ─────────────────────────────────────────────────────── */
  const openCreate = () => { setForm(EMPTY_FORM); setShowModal(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.resellerId) return toast.error('Please select a reseller');
    if (!form.planId)     return toast.error('Please select a plan');
    const count = parseInt(form.count);
    if (isNaN(count) || count < 1 || count > 1000)
      return toast.error('Count must be between 1 and 1000');

    setSaving(true);
    try {
      const res = await activationCodesAPI.create({ ...form, count });
      toast.success(res.data.message);
      setShowModal(false);
      fetchCodes(1);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create codes');
    } finally { setSaving(false); }
  };

  /* ── Delete ─────────────────────────────────────────────────────────────── */
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this activation code?')) return;
    try {
      await activationCodesAPI.delete(id);
      toast.success('Activation code deleted');
      fetchCodes(pagination.page);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    }
  };

  /* ── Copy to clipboard ──────────────────────────────────────────────────── */
  const copyToClipboard = (code) => {
    navigator.clipboard.writeText(code).then(() => {
      setCopiedCode(code);
      toast.success(`Copied: ${code}`);
      setTimeout(() => setCopiedCode(null), 2000);
    });
  };

  const unusedCount = codes.filter(c => !c.isUsed).length;
  const usedCount   = codes.filter(c =>  c.isUsed).length;

  const hasActiveFilter =
    filters.isUsed !== '' || filters.resellerId !== '' || filters.planId !== '';

  /* ── Render ─────────────────────────────────────────────────────────────── */
  return (
    <div className="space-y-5">

      {/* ── Header ── */}
      <div className="flex justify-between items-start gap-4">
        <p className="text-sm text-gray-500 max-w-xl">
          Resellers distribute activation codes to users. Users with a valid code skip payment and are activated immediately.
        </p>
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Export button */}
          <button
            onClick={handleExport}
            disabled={exporting}
            className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50 transition"
          >
            {exporting ? (
              <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
            ) : (
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2M7 10l5 5 5-5M12 15V3" />
              </svg>
            )}
            {exporting ? 'Exporting…' : 'Export CSV'}
            {hasActiveFilter && (
              <span className="ml-0.5 px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded text-xs font-medium">
                filtered
              </span>
            )}
          </button>

          {/* Generate button */}
          <button
            onClick={openCreate}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 transition"
          >
            + Generate Codes
          </button>
        </div>
      </div>

      {/* ── Quick stats ── */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Shown', value: codes.length,  color: 'gray',  icon: '🎟️' },
          { label: 'Available',   value: unusedCount,   color: 'green', icon: '✅' },
          { label: 'Used',        value: usedCount,     color: 'blue',  icon: '✓'  },
        ].map(s => (
          <div key={s.label} className={`bg-${s.color}-50 border border-${s.color}-200 rounded-xl p-4 flex items-center gap-3`}>
            <span className="text-2xl">{s.icon}</span>
            <div>
              <p className={`text-2xl font-bold text-${s.color}-700`}>{s.value}</p>
              <p className={`text-xs text-${s.color}-500`}>{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Filters ── */}
      <div className="flex flex-wrap gap-3 bg-gray-50 border border-gray-200 rounded-xl p-3">
        <select
          value={filters.isUsed}
          onChange={e => setFilters({ ...filters, isUsed: e.target.value })}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">All Status</option>
          <option value="false">Available</option>
          <option value="true">Used</option>
        </select>

        <select
          value={filters.resellerId}
          onChange={e => setFilters({ ...filters, resellerId: e.target.value })}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">All Resellers</option>
          {resellers.map(r => (
            <option key={r._id} value={r._id}>{r.name}{r.company ? ` — ${r.company}` : ''} ({r.email})</option>
          ))}
        </select>

        <select
          value={filters.planId}
          onChange={e => setFilters({ ...filters, planId: e.target.value })}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        >
          <option value="">All Plans</option>
          {plans.map(p => (
            <option key={p._id} value={p._id}>{p.name} ({p.billingCycle})</option>
          ))}
        </select>

        {hasActiveFilter && (
          <button
            onClick={() => setFilters({ isUsed: '', resellerId: '', planId: '' })}
            className="px-3 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-100 transition"
          >
            Clear Filters
          </button>
        )}

        {/* Total count badge */}
        {pagination.total > 0 && (
          <span className="ml-auto self-center text-xs text-gray-400">
            {pagination.total.toLocaleString()} total record{pagination.total !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div className="flex justify-center py-12"><Spinner size="lg" /></div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {['Code', 'Plan', 'Billing', 'Reseller', 'Status', 'Used By', 'Used At', 'Created', 'Actions'].map(h => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {codes.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="text-center py-12 text-gray-400">
                      <p className="text-3xl mb-2">🎟️</p>
                      <p>No activation codes found</p>
                      {hasActiveFilter && (
                        <button
                          onClick={() => setFilters({ isUsed: '', resellerId: '', planId: '' })}
                          className="mt-3 text-indigo-600 text-xs hover:underline"
                        >
                          Clear filters
                        </button>
                      )}
                    </td>
                  </tr>
                ) : codes.map(c => (
                  <tr key={c._id} className="hover:bg-gray-50">
                    {/* Code */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-indigo-700 tracking-widest">{c.code}</span>
                        <button
                          onClick={() => copyToClipboard(c.code)}
                          className="text-gray-400 hover:text-indigo-600 transition"
                          title="Copy code"
                        >
                          {copiedCode === c.code ? '✓' : '📋'}
                        </button>
                      </div>
                    </td>
                    {/* Plan */}
                    <td className="px-4 py-3 font-medium text-gray-800">{c.plan?.name || '—'}</td>
                    {/* Billing */}
                    <td className="px-4 py-3">
                      <span className="capitalize text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full">
                        {c.plan?.billingCycle || '—'}
                      </span>
                    </td>
                    {/* Reseller */}
                    <td className="px-4 py-3">
                      <p className="font-medium text-gray-800">{c.reseller?.name || '—'}</p>
                      <p className="text-xs text-gray-400">{c.reseller?.email}</p>
                    </td>
                    {/* Status */}
                    <td className="px-4 py-3"><StatusBadge isUsed={c.isUsed} /></td>
                    {/* Used By */}
                    <td className="px-4 py-3">
                      {c.usedBy ? (
                        <div>
                          <p className="text-gray-800">{c.usedBy.name}</p>
                          <p className="text-xs text-gray-400">{c.usedBy.email}</p>
                        </div>
                      ) : <span className="text-gray-300">—</span>}
                    </td>
                    {/* Used At */}
                    <td className="px-4 py-3 text-gray-500 text-xs">
                      {c.usedAt ? new Date(c.usedAt).toLocaleDateString() : '—'}
                    </td>
                    {/* Created */}
                    <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                      {new Date(c.createdAt).toLocaleDateString()}
                    </td>
                    {/* Actions */}
                    <td className="px-4 py-3">
                      {!c.isUsed && (
                        <button
                          onClick={() => handleDelete(c._id)}
                          className="text-red-500 hover:text-red-700 text-xs hover:underline"
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* ── Pagination ── */}
          {pagination.pages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <p className="text-xs text-gray-400">
                Page {pagination.page} of {pagination.pages}
                {' '}· {pagination.total.toLocaleString()} total
              </p>
              <Pagination
                currentPage={pagination.page}
                totalPages={pagination.pages}
                onPageChange={(p) => fetchCodes(p)}
              />
            </div>
          )}
        </div>
      )}

      {/* ── Generate Codes Modal ── */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title="Generate Activation Codes" size="md">
        <form onSubmit={handleSubmit} className="space-y-5">

          {/* Reseller */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reseller <span className="text-red-500">*</span>
            </label>
            <select
              value={form.resellerId}
              onChange={e => setForm({ ...form, resellerId: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">— Select a reseller —</option>
              {resellers.map(r => (
                <option key={r._id} value={r._id}>{r.name}{r.company ? ` — ${r.company}` : ''} ({r.email})</option>
              ))}
            </select>
            <p className="text-xs text-gray-400 mt-1">The user (reseller) who will distribute these codes</p>
          </div>

          {/* Plan */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subscription Plan <span className="text-red-500">*</span>
            </label>
            <select
              value={form.planId}
              onChange={e => setForm({ ...form, planId: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">— Select a plan —</option>
              {plans.map(p => (
                <option key={p._id} value={p._id}>
                  {p.name} — {p.billingCycle.charAt(0).toUpperCase() + p.billingCycle.slice(1)}
                </option>
              ))}
            </select>
          </div>

          {/* Count */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Number of Codes <span className="text-red-500">*</span>
            </label>
            <input
              type="number" min="1" max="1000"
              value={form.count}
              onChange={e => setForm({ ...form, count: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <p className="text-xs text-gray-400 mt-1">Default: 10 — Maximum: 1,000 codes per batch</p>
          </div>

          {/* Info */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-xs text-blue-700">
              <strong>How it works:</strong> Users who enter a valid code during signup will be
              activated immediately on the selected plan — no payment required. Each code is single-use.
            </p>
          </div>

          <div className="flex gap-3 pt-2 border-t">
            <button
              type="button" onClick={() => setShowModal(false)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit" disabled={saving}
              className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-60"
            >
              {saving ? 'Generating…' : 'Generate Codes'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ActivationCodes;